import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import { toast } from 'sonner';
import { getMembership, isMembershipActive } from '../utils/membership';
import { loadRazorpayScript, buildRazorpayPrefill, withRazorpayCheckoutConfig } from '../utils/razorpay';
import { useAuth } from '../context/AuthContext';
import NotificationPreferencesPanel from '../components/notifications/NotificationPreferencesPanel';
import DeleteAccountModal from '../components/profile/DeleteAccountModal';
import PurchasesSection from '../components/profile/PurchasesSection';
import TournamentRegistrationsSection from '../components/profile/TournamentRegistrationsSection';
import LegalFooter from '../components/LegalFooter';
import { AddressBook } from '../components/profile/Addresses';
import SportProfilesSection from '../components/sports/SportProfilesSection';
import { formatIndianMobile, isValidIndianMobile } from '../lib/phone';
import { normaliseInstagram, instagramUrl } from '../lib/instagram';

// Gender options for dropdown
const GENDER_OPTIONS = [
  { value: '', label: 'Prefer not to say' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'other', label: 'Other' },
];

// Edit Profile Modal Component
function EditProfileModal({ user, onClose, onSave }) {
  const [formData, setFormData] = useState({
    mobile_number: user?.mobile_number || '',
    instagram_handle: user?.instagram_handle ? `@${user.instagram_handle}` : '',
    name: user?.name || '',
    date_of_birth: user?.date_of_birth || '',
    gender: user?.gender || '',
    city: user?.city || '',
    bio: user?.bio || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.mobile_number && !isValidIndianMobile(formData.mobile_number)) {
      toast.error('Enter a valid 10-digit mobile number');
      return;
    }
    const handle = normaliseInstagram(formData.instagram_handle);
    if (handle === null) {
      toast.error('Enter a valid Instagram handle, e.g. @yourname');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData, instagram_handle: handle };
      if (!payload.mobile_number || payload.mobile_number === user?.mobile_number) delete payload.mobile_number;
      const response = await axiosInstance.patch('/auth/profile', payload);
      onSave(response.data);
      toast.success('Profile updated successfully!');
      onClose();
    } catch (err) {
      console.error('Profile update error:', err);
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1100] p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 20px)' }}
      >
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between pt-safe">
          <h2 className="text-lg font-extrabold text-[#2B3A67]">Edit Profile</h2>
          <button 
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
            data-testid="edit-profile-close"
          >
            <i className="ph-bold ph-x text-gray-500"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Mobile Number */}
          <div>
            <label htmlFor="edit-mobile" className="text-xs font-bold uppercase tracking-widest text-gray-400">
              Mobile Number
            </label>
            <input
              id="edit-mobile"
              data-testid="edit-profile-mobile"
              type="tel"
              inputMode="tel"
              placeholder="+91 98765 43210"
              value={formData.mobile_number}
              onChange={(e) => setFormData({ ...formData, mobile_number: formatIndianMobile(e.target.value) })}
              className="mt-2 w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#E54562]"
            />
          </div>

          {/* Instagram */}
          <div>
            <label htmlFor="edit-instagram" className="text-xs font-bold uppercase tracking-widest text-gray-400">
              Instagram <span className="normal-case tracking-normal font-normal">(optional)</span>
            </label>
            <input
              id="edit-instagram"
              data-testid="edit-profile-instagram"
              type="text"
              autoCapitalize="none"
              autoCorrect="off"
              placeholder="@yourhandle or profile link"
              value={formData.instagram_handle}
              onChange={(e) => setFormData({ ...formData, instagram_handle: e.target.value })}
              className="mt-2 w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#E54562]"
            />
            <p className="text-[11px] text-gray-400 mt-1">
              We may tag you in posts about events you play in and tell you about Backyard events and offers. Clear it to opt out.
            </p>
          </div>

          {/* Email - Read Only */}
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-gray-400">
              Email
            </label>
            <div className="mt-2 w-full border-2 border-gray-100 bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-500">
              {user?.email || 'Not set'}
              <span className="ml-2 text-[10px] text-gray-400">(Cannot be changed)</span>
            </div>
          </div>

          {/* Name */}
          <div>
            <label htmlFor="edit-name" className="text-xs font-bold uppercase tracking-widest text-gray-400">
              Full Name
            </label>
            <input
              id="edit-name"
              data-testid="edit-profile-name"
              type="text"
              placeholder="Your full name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="mt-2 w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#E54562]"
            />
          </div>

          {/* Date of Birth */}
          <div>
            <label htmlFor="edit-dob" className="text-xs font-bold uppercase tracking-widest text-gray-400">
              Date of Birth
            </label>
            <input
              id="edit-dob"
              data-testid="edit-profile-dob"
              type="date"
              value={formData.date_of_birth}
              onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
              max={new Date().toISOString().split('T')[0]}
              className="mt-2 w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#E54562]"
            />
          </div>

          {/* Gender */}
          <div>
            <label htmlFor="edit-gender" className="text-xs font-bold uppercase tracking-widest text-gray-400">
              Gender
            </label>
            <select
              id="edit-gender"
              data-testid="edit-profile-gender"
              value={formData.gender}
              onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              className="mt-2 w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#E54562] bg-white"
            >
              {GENDER_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* City */}
          <div>
            <label htmlFor="edit-city" className="text-xs font-bold uppercase tracking-widest text-gray-400">
              City
            </label>
            <input
              id="edit-city"
              data-testid="edit-profile-city"
              type="text"
              placeholder="Your city"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              className="mt-2 w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#E54562]"
            />
          </div>

          {/* Bio */}
          <div>
            <label htmlFor="edit-bio" className="text-xs font-bold uppercase tracking-widest text-gray-400">
              Bio
            </label>
            <textarea
              id="edit-bio"
              data-testid="edit-profile-bio"
              placeholder="Tell us about yourself..."
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              rows={3}
              maxLength={500}
              className="mt-2 w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#E54562] resize-none"
            />
            <p className="text-[10px] text-gray-400 mt-1 text-right">{formData.bio.length}/500</p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={saving}
            data-testid="edit-profile-save"
            className="w-full py-4 rounded-2xl bg-[#E54562] text-white font-extrabold shadow-lg disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Main ProfilePage ─────────────────────────────────────────────────────────
export default function ProfilePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [serviceBookings, setServiceBookings] = useState([]);
  const [applications, setApplications] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [payingRegistration, setPayingRegistration] = useState(null); // Track which registration is being paid

  // Reload registrations after payment
  const reloadRegistrations = async () => {
    try {
      const res = await axiosInstance.get('/v1/my-registrations');
      setRegistrations(res.data);
    } catch (err) {
      console.error('Failed to reload registrations:', err);
    }
  };

  // Handle Team Owner Payment
  const handleTeamOwnerPayment = async (reg) => {
    setPayingRegistration(reg.id);
    try {
      // Load Razorpay script
      await loadRazorpayScript();

      // Create payment order
      const orderRes = await axiosInstance.post(`/v1/events/${reg.event_id}/team-owner-payment`);
      const orderData = orderRes.data;

      // If free registration, no payment needed
      if (orderData.status === 'approved') {
        toast.success('Registration confirmed!');
        reloadRegistrations();
        setPayingRegistration(null);
        return;
      }

      // Open Razorpay checkout
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: 'INR',
        name: 'Backyard',
        description: `Team Owner Registration - ${reg.event_title || 'Tournament'}`,
        order_id: orderData.order_id,
        handler: async (response) => {
          try {
            // Complete payment
            await axiosInstance.post(`/v1/events/${reg.event_id}/team-owner-payment/complete`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success('Payment successful! Registration confirmed.');
            reloadRegistrations();
          } catch (err) {
            console.error('Payment completion error:', err);
            toast.error(err.response?.data?.detail || 'Payment verification failed. Please contact support.');
          } finally {
            setPayingRegistration(null);
          }
        },
        prefill: buildRazorpayPrefill({
          name: user?.name || '',
          email: user?.email || '',
          contact: user?.mobile_number || '',
        }),
        theme: {
          color: '#2B3A67',
        },
        modal: {
          ondismiss: () => {
            setPayingRegistration(null);
          },
        },
      };

      const razorpayInstance = new window.Razorpay(withRazorpayCheckoutConfig(options));
      razorpayInstance.on('payment.failed', (response) => {
        toast.error(response.error?.description || 'Payment failed');
        setPayingRegistration(null);
      });
      razorpayInstance.open();
    } catch (err) {
      console.error('Payment initiation error:', err);
      toast.error(err.response?.data?.detail || 'Failed to initiate payment');
      setPayingRegistration(null);
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) setUser(JSON.parse(storedUser));

    const loadProfile = async () => {
      setLoading(true);
      const results = await Promise.allSettled([
        axiosInstance.get('/auth/me'),
        axiosInstance.get('/bookings'),
        axiosInstance.get('/orders'),
        axiosInstance.get('/artist-applications'),
        axiosInstance.get('/v1/my-registrations'),
        getMembership(), // Now async - fetches from backend
        axiosInstance.get('/services/bookings/me'),
      ]);
      const [userRes, bookingsRes, ordersRes, appsRes, registrationsRes, membershipRes, serviceBookingsRes] = results;
      if (userRes.status === 'fulfilled') {
        setUser(userRes.value.data);
        localStorage.setItem('user', JSON.stringify(userRes.value.data));
      }
      if (bookingsRes.status === 'fulfilled') setBookings(bookingsRes.value.data);
      if (ordersRes.status === 'fulfilled') setOrders(ordersRes.value.data);
      if (appsRes.status === 'fulfilled') setApplications(appsRes.value.data);
      if (registrationsRes.status === 'fulfilled') setRegistrations(registrationsRes.value.data);
      if (membershipRes.status === 'fulfilled') setMembership(membershipRes.value);
      if (serviceBookingsRes.status === 'fulfilled') setServiceBookings(serviceBookingsRes.value.data);
      if (results.some(r => r.status === 'rejected')) toast.error('Some profile data failed to load');
      setLoading(false);
    };
    loadProfile();
  }, []);

  // Upload profile picture to Cloudinary
  const uploadProfilePicture = async (file) => {
    if (!file) return;
    
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploadingPicture(true);
    try {
      // Get signature for upload
      const sigRes = await axiosInstance.get('/cloudinary/signature?resource_type=image&folder=users/avatars');
      const sig = sigRes.data;

      // Upload to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);

      const uploadRes = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: 'POST', body: formData }
      );

      if (!uploadRes.ok) {
        throw new Error('Upload failed');
      }

      const result = await uploadRes.json();
      
      // Update profile picture URL in backend
      const updateRes = await axiosInstance.patch('/auth/profile-picture', {
        profile_picture_url: result.secure_url
      });
      
      setUser(updateRes.data);
      updateUser(updateRes.data);
      toast.success('Profile picture updated!');
    } catch (err) {
      console.error('Profile picture upload error:', err);
      toast.error('Failed to upload profile picture');
    } finally {
      setUploadingPicture(false);
    }
  };

  const formatDate = ds => new Date(ds).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const membershipActive = isMembershipActive(membership);
  const membershipTitle = membershipActive ? membership.plan_name : 'Backyard Regular';
  const membershipBalance = membershipActive ? (membership.balance_remaining ?? membership.price) : 0;

  const { logout, updateUser } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (e) {
      // Even if Firebase signOut fails, clear local state
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    toast.success('Logged out successfully');
    navigate('/auth');
  };

  const userRoles = user?.roles || (user?.role ? [user.role] : ['regular']);
  const isAdmin = userRoles.includes('admin') || userRoles.includes('super_admin');
  const isSuperAdmin = userRoles.includes('super_admin');

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-sand flex items-center justify-center pb-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E54562]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-28 text-[#2B3A67]" style={{ fontFamily: "'Outfit', sans-serif" }}>

      {/* Edit Profile Modal */}
      {showEditProfile && (
        <EditProfileModal 
          user={user} 
          onClose={() => setShowEditProfile(false)}
          onSave={(updatedUser) => {
            setUser(updatedUser);
            updateUser(updatedUser);
          }}
        />
      )}

      {/* Header hero card */}
      <header className="px-6 pt-8 pb-4">
        <div className="bg-gradient-to-br from-[#2B3A67] via-[#3AAFA9] to-[#F4D341] rounded-[2rem] p-6 text-white shadow-xl" data-testid="profile-hero">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="text-xs uppercase tracking-widest text-white/70">Profile</p>
                <button 
                  onClick={() => setShowEditProfile(true)}
                  className="text-[10px] px-2 py-0.5 rounded-full bg-white/20 text-white/90 hover:bg-white/30 transition-colors"
                  data-testid="edit-profile-btn"
                >
                  <i className="ph-bold ph-pencil-simple mr-1"></i>Edit
                </button>
              </div>
              <h1 data-testid="profile-title" className="text-2xl font-extrabold mt-1">
                {user?.name || 'Guest'}
              </h1>
              <p data-testid="profile-user-email" className="text-xs text-white/80 mt-1">
                {user?.email || '—'}
              </p>
              {user?.mobile_number && (
                <p data-testid="profile-user-mobile" className="text-xs text-white/80">
                  {user.mobile_number}
                </p>
              )}
            </div>
            {/* Profile Picture with Upload */}
            <label className="relative cursor-pointer group">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-xl font-black overflow-hidden border-2 border-white/30">
                {user?.profile_picture_url ? (
                  <img 
                    src={user.profile_picture_url} 
                    alt={user.name} 
                    className="w-full h-full object-cover"
                    data-testid="profile-avatar-image"
                  />
                ) : (
                  <span data-testid="profile-avatar-initials">
                    {(user?.name || 'BY').slice(0, 2).toUpperCase()}
                  </span>
                )}
              </div>
              {/* Upload overlay */}
              <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                {uploadingPicture ? (
                  <i className="ph-bold ph-spinner animate-spin text-white text-xl"></i>
                ) : (
                  <i className="ph-bold ph-camera text-white text-xl"></i>
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => e.target.files[0] && uploadProfilePicture(e.target.files[0])}
                disabled={uploadingPicture}
                data-testid="profile-picture-upload"
              />
            </label>
          </div>

          {/* Stats */}
          <div className="mt-6 grid grid-cols-3 gap-3 text-center">
            <div className="bg-white/15 rounded-2xl py-3">
              <p className="text-xs uppercase tracking-widest text-white/70">Tickets</p>
              <p data-testid="profile-bookings-count" className="text-xl font-extrabold">{bookings.length}</p>
            </div>
            <div className="bg-white/15 rounded-2xl py-3">
              <p className="text-xs uppercase tracking-widest text-white/70">Purchases</p>
              <p data-testid="profile-orders-count" className="text-xl font-extrabold">{orders.length + serviceBookings.length}</p>
            </div>
            <div className="bg-white/15 rounded-2xl py-3">
              <p className="text-xs uppercase tracking-widest text-white/70">Credits</p>
              <p data-testid="profile-membership-balance" className="text-xl font-extrabold">₹{membershipBalance}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="px-6 space-y-6">

        {/* ── Profile Details ── */}
        <section className="bg-white rounded-2xl p-5 shadow-md border border-gray-100" data-testid="profile-details">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-extrabold">Profile Details</h3>
            <button 
              onClick={() => setShowEditProfile(true)}
              className="text-sm font-bold text-[#E54562]"
              data-testid="edit-profile-link"
            >
              Edit
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Mobile</p>
              <p className="font-medium" data-testid="profile-detail-mobile">{user?.mobile_number || 'Not set'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Gender</p>
              <p className="font-medium capitalize" data-testid="profile-detail-gender">{user?.gender?.replace('_', ' ') || 'Not set'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Date of Birth</p>
              <p className="font-medium" data-testid="profile-detail-dob">
                {user?.date_of_birth ? new Date(user.date_of_birth).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not set'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">City</p>
              <p className="font-medium" data-testid="profile-detail-city">{user?.city || 'Not set'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Instagram</p>
              {user?.instagram_handle ? (
                <a href={instagramUrl(user.instagram_handle)} target="_blank" rel="noreferrer"
                  className="font-medium text-[#2B3A67] underline" data-testid="profile-detail-instagram">
                  @{user.instagram_handle}
                </a>
              ) : <p className="font-medium" data-testid="profile-detail-instagram">Not set</p>}
            </div>
            {user?.bio && (
              <div className="col-span-2">
                <p className="text-xs text-gray-400 uppercase tracking-wide">Bio</p>
                <p className="font-medium text-gray-600" data-testid="profile-detail-bio">{user.bio}</p>
              </div>
            )}
          </div>
        </section>

        {/* ── Saved Addresses ── */}
        <section className="bg-white rounded-2xl p-5 shadow-md border border-gray-100" data-testid="profile-addresses">
          <h3 className="font-extrabold mb-1">Saved Addresses</h3>
          <p className="text-xs text-gray-400 mb-4">Used for shop deliveries.</p>
          <AddressBook />
        </section>

        {/* ── Sports Profiles ── */}
        <SportProfilesSection />

        {/* ── Membership ── */}
        <section className="bg-white rounded-2xl p-5 shadow-md border border-gray-100" data-testid="profile-membership">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400">Membership</p>
              <h2 data-testid="profile-membership-title" className="text-lg font-extrabold mt-1">{membershipTitle}</h2>
              <p className="text-xs text-gray-500 mt-1" data-testid="profile-membership-status">
                {membershipActive ? 'Active' : 'Inactive'}
              </p>
            </div>
            <button onClick={() => navigate('/memberships')}
              className="px-4 py-2 rounded-xl bg-[#E54562] text-white font-bold text-xs"
              data-testid="profile-membership-cta">
              Manage
            </button>
          </div>

          {membershipActive && (() => {
            const total = membership.credits_total || membership.price || 0;
            const balance = membership.balance_remaining ?? 0;
            const used = Math.max(total - balance, 0);
            const pct = total > 0 ? Math.max(0, Math.min(100, (balance / total) * 100)) : 0;
            return (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-baseline justify-between mb-2">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Credits</p>
                  <p className="text-xs font-bold text-gray-400">
                    {used > 0 ? `${used.toLocaleString('en-IN')} used` : 'unused'}
                  </p>
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-2xl font-black text-[#E54562]" data-testid="profile-credits-balance">
                    ₹{balance.toLocaleString('en-IN')}
                  </span>
                  <span className="text-sm font-bold text-gray-400">
                    / ₹{total.toLocaleString('en-IN')} total
                  </span>
                </div>
                <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden" data-testid="profile-credits-bar">
                  <div
                    className="h-full bg-gradient-to-r from-[#E54562] to-[#F4D341] transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <p className="text-[11px] text-gray-400 mt-2">
                  Expires {new Date(membership.expires_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </p>
              </div>
            );
          })()}
        </section>

        {/* ── My Tickets ── */}
        <section className="bg-white rounded-2xl p-5 shadow-md border border-gray-100" data-testid="profile-tickets">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-extrabold">My Tickets</h3>
            <button onClick={() => navigate('/tickets')} className="text-sm font-bold text-[#E54562]"
              data-testid="profile-tickets-link">View All</button>
          </div>
          {bookings.length === 0 ? (
            <p data-testid="profile-bookings-empty" className="text-sm text-gray-400">
              No bookings yet. Browse events to get started.
            </p>
          ) : (
            <div className="bg-gray-50 rounded-xl p-4">
              <p data-testid="profile-booking-title-0" className="font-bold">{bookings[0].event_title}</p>
              <p data-testid="profile-booking-date-0" className="text-xs text-gray-500">
                {formatDate(bookings[0].event_date)} · {bookings[0].venue_name}
              </p>
              <button onClick={() => navigate(`/bookings/${bookings[0].id}`)}
                className="mt-3 px-4 py-2 rounded-lg bg-[#2B3A67] text-white text-xs font-bold"
                data-testid="profile-view-booking-0">
                View Ticket
              </button>
            </div>
          )}
        </section>

        {/* ── My Tournament Registrations ── */}
        <TournamentRegistrationsSection
          registrations={registrations}
          payingRegistrationId={payingRegistration}
          onPay={handleTeamOwnerPayment}
        />

        {/* ── My Purchases (Services + Shop Orders) ── */}
        <PurchasesSection
          serviceBookings={serviceBookings}
          orders={orders}
        />

        {/* ── Account Actions ── */}
        <section className="bg-white rounded-2xl p-5 shadow-md border border-gray-100" data-testid="profile-actions">
          <h3 className="font-extrabold mb-4">Account Actions</h3>
          <div className="space-y-3">
            <button onClick={() => navigate('/create')}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-gray-50 font-bold text-sm"
              data-testid="profile-create-button">
              Create Application
              <i className="ph-bold ph-arrow-right" />
            </button>
            <button onClick={() => navigate('/memberships')}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-gray-50 font-bold text-sm"
              data-testid="profile-membership-button">
              Memberships
              <i className="ph-bold ph-arrow-right" />
            </button>
            <button onClick={() => navigate('/tickets')}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-gray-50 font-bold text-sm"
              data-testid="profile-tickets-button">
              Tickets
              <i className="ph-bold ph-arrow-right" />
            </button>

            {/* Admin / Super Admin dashboard button */}
            {isAdmin && (
              <button data-testid="admin-dashboard-button"
                onClick={() => navigate('/admin')}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl font-bold text-sm text-white ${isSuperAdmin ? 'bg-gradient-to-r from-[#E54562] to-[#2B3A67]' : 'bg-[#2B3A67]'}`}>
                {isSuperAdmin ? 'Super Admin Panel' : 'Admin Dashboard'}
                <i className={`ph-bold ${isSuperAdmin ? 'ph-crown' : 'ph-shield-check'}`} />
              </button>
            )}
          </div>
        </section>

        {/* ── Notification Preferences ── */}
        <NotificationPreferencesPanel />

        <button onClick={handleLogout}
          className="w-full py-4 rounded-2xl bg-[#E54562] text-white font-extrabold"
          data-testid="profile-logout-button">
          Logout
        </button>

        <button
          onClick={() => setShowDeleteAccount(true)}
          className="w-full py-3 rounded-2xl bg-white border-2 border-rose-200 text-rose-600 font-bold text-sm hover:bg-rose-50"
          data-testid="profile-delete-account-button">
          Delete Account
        </button>
      </main>

      {showDeleteAccount && (
        <DeleteAccountModal onClose={() => setShowDeleteAccount(false)} />
      )}

      <LegalFooter />
    </div>
  );
}
